import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import logo from '../../assets/logos/AI Analytics logo.svg'
import {
  AppShell,
  Text,
  Burger,
  Group,
  Box,
  UnstyledButton,
  Avatar,
  Menu,
  useMantineTheme,
  ActionIcon,
  TextInput,
  Stack,
  Badge,
  Paper
} from '@mantine/core'
import {
  IconLayoutDashboard,
  IconBook,
  IconUsers,
  IconChartBar,
  IconSettings,
  IconChevronDown,
  IconLogout,
  IconBell,
  IconSearch,
  IconQuestionMark,
  IconHelp,
  IconUser
} from '@tabler/icons-react'
import { useAuth } from '../../contexts/AuthContext'
import { organizationService } from '../../services'
import styles from './AdminLayout.module.css'

const navItems = [
  { icon: IconLayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: IconBook, label: 'Courses', path: '/admin/courses' },
  { icon: IconUsers, label: 'Learners', path: '/admin/learners' },
  { icon: IconQuestionMark, label: 'Questions', path: '/admin/questions' },
  { icon: IconChartBar, label: 'Reports', path: '/admin/reports' },
  { icon: IconSettings, label: 'Settings', path: '/admin/settings' },
]

function NavbarLink({ icon: Icon, label, path, onClick }) {
  const location = useLocation()
  const navigate = useNavigate()
  const isActive = location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <UnstyledButton
      onClick={() => {
        navigate(path)
        onClick?.()
      }}
      p="10px 12px"
      w="100%"
      bd="none"
      bg={isActive ? 'blue.0' : 'transparent'}
      c={isActive ? 'blue.6' : 'gray.6'}
      className={styles.navLink}
    >
      <Group gap="xs">
        <Icon size={20} />
        <Text size="sm">{label}</Text>
      </Group>
    </UnstyledButton>
  )
}

function UserAvatar({ src, name, ...props }) {
  return <Avatar
    src={src}
    alt={name}
    size={35}
    radius="xl"
    {...props}
  />
}

export function AdminLayout({ children }) {
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [opened, setOpened] = useState(false)
  const [organization, setOrganization] = useState(null)

  useEffect(() => {
    if (user?.email) {
      organizationService.getUserOrganization(user.email).then(org => {
        setOrganization(org)
      })
    }
  }, [user])

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ 
        width: 250, 
        breakpoint: 'sm',
        collapsed: { mobile: !opened }
      }}
      padding="md"
      bg="gray.0"
    >
      <AppShell.Header bg="white" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={opened}
              onClick={() => setOpened((o) => !o)}
              hiddenFrom="sm"
              size="sm"
            />
            <Text size="lg" fw={500} visibleFrom="sm">
              Course Management
            </Text>
          </Group>

          <Group>
            <ActionIcon
              size="lg"
              variant="subtle"
              radius="md"
              onClick={() => navigate('/help')}
              title="Help Center"
            >
              <IconHelp size={20} />
            </ActionIcon>
            
            <ActionIcon size="lg" variant="subtle" radius="md">
              <IconBell size={20} />
            </ActionIcon>

            <Menu
              width={200}
              position="bottom-end"
              transitionProps={{ transition: 'pop-top-right' }}
            >
              <Menu.Target>
                <UnstyledButton>
                  <Group gap={7}>
                    <UserAvatar src={user?.avatar} name={user?.name} />
                    <Box hiddenFrom="sm">
                      <Text size="sm" fw={500}>{user?.name}</Text>
                      <Badge size="xs" variant="light" color="grape">Admin</Badge>
                    </Box>
                    <IconChevronDown size={12} stroke={1.5} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>{user?.name}</Text>
                    <Text size="xs" c="dimmed" style={{ 
                      maxWidth: '180px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis' 
                    }}>
                      {user?.email}
                    </Text>
                  </Stack>
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item 
                  leftSection={<IconUser size={14} stroke={1.5} />}
                  onClick={() => navigate('/admin/profile')}
                >
                  My Profile
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconSettings size={14} stroke={1.5} />}
                  onClick={() => navigate('/admin/settings')}
                >
                  Company Settings
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconHelp size={14} stroke={1.5} />}
                  onClick={() => navigate('/help')}
                >
                  Help Center
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item 
                  leftSection={<IconLogout size={14} stroke={1.5} />}
                  onClick={async () => {
                    await signOut()
                  }}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" bg="white">
        <AppShell.Section>
          <Group mb="xl" gap="xs">
            <img 
              src={logo} 
              alt="AI Analytics" 
              height="40"
              width="auto"
            />
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>Learning and Marketing</Text>
            </Box>
          </Group>
        </AppShell.Section>

        <AppShell.Section grow>
          <Stack gap={0}>
            {navItems.map((item) => (
              <NavbarLink
                key={item.label}
                {...item}
                onClick={() => setOpened(false)}
              />
            ))}
          </Stack>
        </AppShell.Section>

        <AppShell.Section>
          <Paper
            p="md"
            bg="gray.1"
            radius="md"
            ta="center"
          >
            <Text size="sm" fw={500} mb="xs">{organization?.name || 'Loading...'}</Text>
            <Badge size="sm" variant="light" color="grape">Enterprise Plan</Badge>
          </Paper>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {children || <Outlet />}
      </AppShell.Main>
    </AppShell>
  )
}